import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
} from '../../../shared/workspace-element-context';
import { Form1118SchdKService } from './form-1118-schd-k.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-form-1118-schd-k',
    templateUrl: './form-1118-schd-k.component.html',
    styleUrls: ['./form-1118-schd-k.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class Form1118SchdKComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // ---- Global / Input state ----
  globalService: any;
  GENERAL_CONFIG: any;
  USER_SETTINGS: any;
  filterParams: any = {};

  private isInitialized = false;

  // ---- User info ----
  logged_in_user: string = '';

  // ---- Filter dropdowns ----
  filteredTaxYearData: any[] = [];
  selectedTaxYear: any;
  scenarioDropDown: any[] = [];
  selectedScenario: any;
  filingGroupDropdown: any[] = [];
  selectedFG: any;
  filerEntityDropdown: any[] = [];
  selectedFE: any;

  // ---- Loading flags ----
  filterLoading = false;
  scenarioLoading = false;
  filingGrpLoading = false;
  filerEntityLoading = false;
  showSpinner = true;
  showBanner = true;

  // ---- Form data ----
  formLines: any[] = [];
  groupedFormLines: any[] | null = null;
  formColumns: any[] = [];

  // ---- Keys ----
  jcd_key: string = '';
  jcd_ta_key: string = '';
  sourceKey: string = '';

  // ---- Admin / Save ----
  isAdmin = false;
  isSaveClicked = false;

  // ---- Activity ----
  activity_name: string = '';
  scenario: string = '';
  curentyear: string = '';

  // ---- Confirmation Modal ----
  showConfirmDialog: boolean = false;
  modalConfig: ConfirmationModalConfig = {
    title: '',
    message: ''
  };

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private form1118SchdKService: Form1118SchdKService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private el: ElementRef
  ) { }

  ngOnInit(): void {
    this.gtwWorkspace.hideDataFilters.next(true);
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.globalService = { ...header, inputs: this.gtwGlobal.inputs };
    this.filterParams = normalizeFilterParams(activeScreen);
    this.cdr.detectChanges();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (this.globalService && this.GENERAL_CONFIG) {
      this.isInitialized = true;
      this.init();
    }
  }

  // ===================== INITIALISATION =====================

  init(): void {
    this.scenario = this.filterParams?.scenario || this.globalService?.globalParams?.scenario || '';
    this.jcd_key = this.filterParams?.jcd_key || this.globalService?.globalParams?.jcd_key || '';
    this.jcd_ta_key = this.filterParams?.jcd_ta_key || this.globalService?.globalParams?.jcd_ta_key || '';
    this.activity_name = this.filterParams?.activity_name || this.globalService?.activity_name || this.globalService?.globalParams?.activity_name || '';
    this.curentyear = this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year || '';

    // Load tax year dropdown
    this.getTaxYearDropDown();
  }

  // ===================== FILTER DROPDOWNS =====================

  getTaxYearDropDown(): void {
    const taxYearInputs = this.globalService?.inputs?.tax_year;
    if (taxYearInputs) {
      this.filteredTaxYearData = taxYearInputs.values || [];
      this.selectedTaxYear = taxYearInputs.selected || this.filteredTaxYearData[0];
    } else {
      this.filteredTaxYearData = [{ value: this.curentyear, label: this.curentyear }];
      this.selectedTaxYear = this.filteredTaxYearData[0];
    }
    this.getScenarioDropDown();
  }

  getScenarioDropDown(): void {
    if (!this.selectedTaxYear) return;
    this.scenarioLoading = true;
    this.cdr.detectChanges();

    this.form1118SchdKService.getScenarioDropDown(
      this.GENERAL_CONFIG.base_url,
      this.selectedTaxYear.value,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.scenarioDropDown = data?.jsonObject || [];
        this.selectedScenario = this.scenarioDropDown[0];
        this.scenarioLoading = false;
        this.filterLoading = false;
        this.getFilingGroupDropdown();
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading scenario dropdown:', error);
        this.scenarioLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  getFilingGroupDropdown(): void {
    if (!this.selectedTaxYear || !this.selectedScenario) return;
    this.filingGrpLoading = true;
    this.cdr.detectChanges();

    this.form1118SchdKService.getFilingGroupDropdown(
      this.GENERAL_CONFIG.base_url,
      this.selectedTaxYear.value,
      this.selectedScenario.VALUE,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.filingGroupDropdown = data?.jsonObject || [];
        this.selectedFG = this.filingGroupDropdown[0];
        this.filingGrpLoading = false;
        this.getFilerEntityDropdown();
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading filing group dropdown:', error);
        this.filingGrpLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  getFilerEntityDropdown(): void {
    if (!this.selectedFG || this.selectedFG.VALUE === '') return;
    this.filerEntityLoading = true;
    this.cdr.detectChanges();

    this.form1118SchdKService.getFilerEntityDropdown(
      this.GENERAL_CONFIG.base_url,
      this.selectedTaxYear.value,
      this.selectedScenario.VALUE,
      this.selectedFG.VALUE,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        const filerEntityArr = data?.jsonObject || [];
        this.filerEntityDropdown = _.filter(filerEntityArr, { CONSOL_GROUP_KEY: this.selectedFG.VALUE });
        this.selectedFE = this.filerEntityDropdown[0];
        this.filerEntityLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading filer entity dropdown:', error);
        this.filerEntityLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  // ===================== REFRESH / FILTER =====================

  refresh(): void {
    if (!this.selectedFE || this.selectedFE.FILER_LE_KEY === '') {
      this.gtwSnackbarService.openSnackBar('Please select all the filters.', 'gtw-snackbar--red');
      return;
    }
    this.showBanner = false;
    this.showSpinner = true;
    this.cdr.detectChanges();
    this.getFormColumnDescriptions();
    this.getFormLinesAndDescriptions();
  }

  // ===================== FORM DATA LOADING =====================

  getFormColumnDescriptions(): void {
    this.form1118SchdKService.getFormColumnDescriptions(
      this.GENERAL_CONFIG.base_url,
      this.selectedTaxYear.value,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.formColumns = data?.jsonObject || [];
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading column descriptions:', error);
      }
    );
  }

  getFormLinesAndDescriptions(): void {
    // Determine basket based on activity name
    let basketToFetch = 'GENLIM';
    const activityName = this.activity_name;

    if (activityName === 'General') {
      basketToFetch = 'GENLIM';
    } else if (activityName === 'Passive') {
      basketToFetch = 'PASSIVE';
    } else if (activityName === 'Branch') {
      basketToFetch = 'BRANCH';
    } else if (activityName === 'GILTI') {
      basketToFetch = 'GILTI';
    } else if (activityName === 'Treaty') {
      basketToFetch = 'TREATY';
    } else if (activityName === '901j') {
      basketToFetch = '901j';
    }

    this.form1118SchdKService.getFormLinesAndDescriptions(
      this.GENERAL_CONFIG.base_url,
      basketToFetch,
      this.jcd_key,
      this.jcd_ta_key,
      this.selectedTaxYear.value,
      this.selectedScenario.VALUE,
      this.selectedFE.FILER_LE_KEY,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.formLines = data?.jsonObject || [];
        this.processFormLines();
        this.cdr.detectChanges();

        // Format editable inputs with commas after view renders (matching AngularJS format="number")
        setTimeout(() => this.formatAllInputs(), 0);
      },
      (error: any) => {
        console.error('Error loading form lines:', error);
        this.showSpinner = false;
        this.cdr.detectChanges();
      }
    );
  }

  // ===================== DATA GROUPING =====================

  private processFormLines(): void {
    if (!this.formLines || this.formLines.length === 0) {
      this.showSpinner = false;
      return;
    }

    let init = 0;

    // Assign is_admin flag
    if (this.formLines[0].IS_ADMIN === 'Y') {
      this.isAdmin = true;
    }

    this.groupedFormLines = _.reduce(
      this.formLines,
      (memo: any[], property: any) => {
        let bucket: any = _.find(memo, {
          SCHD_KEY: property.SCHD_KEY,
          LINE_KEY: property.LINE_KEY,
          LINE_NO: property.LINE_NO,
          LINE_DESCRIPTION: property.LINE_DESCRIPTION,
          LINE_ORDER: property.LINE_ORDER,
          BASKET_KEY: property.BASKET_KEY,
          BASKET_DESC: property.BASKET_DESC
        });

        if (!bucket) {
          bucket = {
            SCHD_KEY: property.SCHD_KEY,
            LINE_KEY: property.LINE_KEY,
            LINE_NO: property.LINE_NO,
            LINE_DESCRIPTION: property.LINE_DESCRIPTION,
            LINE_ORDER: property.LINE_ORDER,
            BASKET_KEY: property.BASKET_KEY,
            BASKET_DESC: property.BASKET_DESC,
            items: []
          };
          memo.push(bucket);
        }

        // Coerce AMOUNT to a real number to prevent string concatenation in calculations
        const rawAmount = property.AMOUNT;
        const numericAmount = (rawAmount != null && rawAmount !== '') ? Number(rawAmount) : null;

        const attribs: any = {
          SCHD_KEY: property.SCHD_KEY,
          LINE_KEY: property.LINE_KEY,
          BASKET_KEY: property.BASKET_KEY,
          COL_KEY: property.COL_KEY,
          COL_DESCRIPTION: property.COL_DESCRIPTION,
          COL_ORDER: property.COL_ORDER,
          IS_ENABLED: property.IS_DISPLAY,
          ATTRIB_AMOUNT: (numericAmount !== null && !isNaN(numericAmount)) ? numericAmount : null
        };

        if (init === 0 && attribs.BASKET_KEY) {
          init = 1;
          this.sourceKey = attribs.BASKET_KEY;
        }

        if (attribs.IS_ENABLED === 'N') {
          attribs.OPERATION_TYPE = null;
        }
        if (attribs.ATTRIB_AMOUNT != null) {
          attribs.OPERATION_TYPE = 'U';
        }

        bucket.items.push(attribs);
        return memo;
      },
      []
    );

    this.addTotals();
    this.showSpinner = false;
  }

  // ===================== TOTALS CALCULATION =====================

  /**
   * Safely coerce any value to a number.
   * Handles strings from ngModel (type="text"), null, undefined, NaN.
   * Matches AngularJS behaviour where format="number" kept values numeric.
   */
  private toNum(val: any): number {
    if (val == null || val === '') return 0;
    const n = Number(val);
    return isNaN(n) ? 0 : n;
  }

  addTotals(): void {
    if (!this.groupedFormLines) return;

    for (const line of this.groupedFormLines) {
      // Calculate Line# 3 amounts
      if (line.LINE_NO.startsWith('3')) {
        const index1 = _.findIndex(this.groupedFormLines, { LINE_NO: '1.' });
        const index2a = _.findIndex(this.groupedFormLines, { LINE_NO: '2a.' });
        const index2b = _.findIndex(this.groupedFormLines, { LINE_NO: '2b.' });
        const index3 = _.findIndex(this.groupedFormLines, { LINE_NO: '3.' });
        const index4 = _.findIndex(this.groupedFormLines, { LINE_NO: '4.' });
        const index5 = _.findIndex(this.groupedFormLines, { LINE_NO: '5.' });
        const index6 = _.findIndex(this.groupedFormLines, { LINE_NO: '6.' });
        const index7 = _.findIndex(this.groupedFormLines, { LINE_NO: '7.' });
        const index8 = _.findIndex(this.groupedFormLines, { LINE_NO: '8.' });

        if (index3 >= 0 && index1 >= 0 && index2a >= 0 && index2b >= 0) {
          for (let i = 0; i < this.groupedFormLines[index3].items.length; i++) {
            // Line 3 = Line 1 + Line 2a + Line 2b
            if (this.groupedFormLines[index3].items[i].IS_ENABLED === 'Y') {
              this.groupedFormLines[index3].items[i].ATTRIB_AMOUNT =
                this.toNum(this.groupedFormLines[index1].items[i].ATTRIB_AMOUNT) +
                this.toNum(this.groupedFormLines[index2a].items[i].ATTRIB_AMOUNT) +
                this.toNum(this.groupedFormLines[index2b].items[i].ATTRIB_AMOUNT);

              if (this.groupedFormLines[index3].items[i].ATTRIB_AMOUNT === 0) {
                this.groupedFormLines[index3].items[i].ATTRIB_AMOUNT = null;
              }
            } else {
              this.groupedFormLines[index3].items[i].ATTRIB_AMOUNT = null;
            }

            // Line 8 = Line 3 + Line 4 + Line 5 + Line 6 + Line 7
            if (index8 >= 0 && index4 >= 0 && index5 >= 0 && index6 >= 0 && index7 >= 0) {
              if (this.groupedFormLines[index8].items[i].IS_ENABLED === 'Y') {
                this.groupedFormLines[index8].items[i].ATTRIB_AMOUNT =
                  this.toNum(this.groupedFormLines[index3].items[i].ATTRIB_AMOUNT) +
                  this.toNum(this.groupedFormLines[index4].items[i].ATTRIB_AMOUNT) +
                  this.toNum(this.groupedFormLines[index5].items[i].ATTRIB_AMOUNT) +
                  this.toNum(this.groupedFormLines[index6].items[i].ATTRIB_AMOUNT) +
                  this.toNum(this.groupedFormLines[index7].items[i].ATTRIB_AMOUNT);

                if (this.groupedFormLines[index8].items[i].ATTRIB_AMOUNT === 0) {
                  this.groupedFormLines[index8].items[i].ATTRIB_AMOUNT = null;
                }
              } else {
                this.groupedFormLines[index8].items[i].ATTRIB_AMOUNT = null;
              }
            }
          }
        }
      }

      // Calculate row-level "Totals" column (sum across all year columns)
      let totalAmount = 0;
      for (const item of line.items) {
        if (item.COL_DESCRIPTION !== 'Totals') {
          totalAmount += this.toNum(item.ATTRIB_AMOUNT);
        }
      }
      const totalColIndex = _.findIndex(line.items, { COL_DESCRIPTION: 'Totals' });
      if (totalColIndex >= 0) {
        line.items[totalColIndex].ATTRIB_AMOUNT = (totalAmount === 0) ? null : totalAmount;
      }
    }
  }

  // ===================== RESET =====================

  reset(): void {
    if (!this.groupedFormLines) return;
    for (const line of this.groupedFormLines) {
      for (const item of line.items) {
        item.ATTRIB_AMOUNT = null;
      }
    }
    this.showSpinner = true;
    this.cdr.detectChanges();
    this.getFormLinesAndDescriptions();
  }

  // ===================== SAVE (with gtw-confirmation-modal) =====================

  saveGeneralBasket(): void {
    this.modalConfig = {
      title: '1118 Schedule K',
      message: `Are you sure to save Schedule K data for Filer Entity: ${this.selectedFE?.FILER_LE || ''}`,
      iconClass: 'fa fa-sign-out',
      iconColor: 'txt-color-orange-dark',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      confirmButtonClass: 'btn-danger',
      cancelButtonClass: 'btn-default',
      showCancelButton: true,
      showConfirmButton: true,
      animationClass: 'animated fadeIn fast',
      loading: false,
      loadingText: 'Processing...'
    };
    this.showConfirmDialog = true;
    this.cdr.detectChanges();
  }

  onConfirmSave(): void {
    this.showConfirmDialog = false;
    this.cdr.detectChanges();
    this.performSave();
  }

  onCancelConfirm(): void {
    this.showConfirmDialog = false;
    this.cdr.detectChanges();
  }

  private performSave(): void {
    this.isSaveClicked = true;
    this.cdr.detectChanges();

    if (!this.groupedFormLines) return;

    const dataToSave: any[] = [];

    const returnClobSettingsObj: any = {
      sso_id: this.logged_in_user,
      jcd_key: this.jcd_key,
      jcd_ta_key: this.jcd_ta_key
    };

    for (const line of this.groupedFormLines) {
      for (const item of line.items) {
        const returnObj: any = {
          AMOUNT: item.ATTRIB_AMOUNT,
          REASON: null,
          SCHD_KEY: item.SCHD_KEY,
          LINE_KEY: item.LINE_KEY,
          COL_KEY: item.COL_KEY,
          BASKET_KEY: item.BASKET_KEY || this.sourceKey,
          OPERATION_TYPE: null
        };

        if (item.ATTRIB_AMOUNT != null && item.ATTRIB_AMOUNT !== 0 && item.OPERATION_TYPE === 'U') {
          returnObj.OPERATION_TYPE = item.OPERATION_TYPE;
        } else if (item.ATTRIB_AMOUNT != null && item.OPERATION_TYPE == null) {
          returnObj.OPERATION_TYPE = 'I';
          item.OPERATION_TYPE = 'U';
        } else if ((item.ATTRIB_AMOUNT == null || item.ATTRIB_AMOUNT === 0) && item.OPERATION_TYPE === 'U') {
          returnObj.OPERATION_TYPE = 'D';
        }

        if (returnObj.OPERATION_TYPE != null) {
          dataToSave.push(returnObj);
        }
      }
    }

    const message = 'Form 1118 Schedule-K Amount(s) have been saved/updated';
    this.sendDetails(returnClobSettingsObj, dataToSave, message);
  }

  private sendDetails(returnClobSettingsObj: any, returnClobDataObj: any[], message: string): void {
    this.form1118SchdKService.saveBasketAmounts(
      this.GENERAL_CONFIG.base_url,
      returnClobSettingsObj,
      returnClobDataObj,
      this.jcd_key,
      this.selectedTaxYear.value,
      this.selectedScenario.VALUE + '',
      this.selectedFE.FILER_LE_KEY,
      this.selectedFE.FILER_COMBINATION_KEY,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        // saveJSON returns response body directly; callSuccess is '1' (string) on success
        if (result?.callSuccess == '1') {
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
        } else {
          this.gtwSnackbarService.openSnackBar(
            result?.errorMessage || 'Error occurred while saving.',
            'gtw-snackbar--red'
          );
        }
        this.isSaveClicked = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.isSaveClicked = false;
        this.cdr.detectChanges();
      }
    );
  }

  // ===================== HELPERS =====================

  trackByLineOrder(index: number, item: any): any {
    return item.LINE_ORDER || index;
  }

  trackByIndex(index: number): number {
    return index;
  }

  /**
   * After data loads and the view renders, format every visible editable text
   * input so the user sees comma-separated values on first paint —
   * replicating the AngularJS format="number" $formatters behaviour.
   */
  private formatAllInputs(): void {
    const inputs = this.el.nativeElement.querySelectorAll('input[type="text"]');
    inputs.forEach((input: HTMLInputElement) => {
      if (input.disabled) return;
      const raw = (input.value || '').replace(/,/g, '');
      if (raw !== '' && raw !== '-') {
        const num = parseFloat(raw);
        if (!isNaN(num)) {
          input.value = num.toLocaleString('en-US');
        }
      }
    });
  }

  /**
   * Format number with commas for display (matching AngularJS numberWithCommas filter).
   * Negative values are wrapped in parentheses: -1234 → (1,234)
   */
  formatNumberWithCommas(value: any): string {
    if (value == null || value === '') return '';
    const num = Number(value);
    if (isNaN(num)) return '';
    let formatted = Math.abs(num).toLocaleString('en-US');
    if (num < 0) {
      formatted = '(' + formatted + ')';
    }
    return formatted;
  }

  // ===================== FIELD FORMATTING (comma-separated numbers) =====================

  /**
   * On focus: strip commas so the user edits the raw number.
   */
  onFieldFocus(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/,/g, '');
  }

  /**
   * On blur: re-format the value with commas.
   */
  onFieldBlur(event: FocusEvent, model: any, key: string): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value;
    if (raw !== '' && raw !== '-') {
      const num = parseFloat(raw);
      if (!isNaN(num)) {
        input.value = num.toLocaleString('en-US');
      }
    }
  }

  /**
   * On keypress: only allow digits, decimal point, and minus sign.
   */
  onFieldKeypress(event: KeyboardEvent): void {
    const pattern = /[0-9.\-]/;
    const inputChar = String.fromCharCode(event.charCode);
    if (!pattern.test(inputChar)) {
      event.preventDefault();
    }
  }

  /**
   * On input: sanitize value – strip non-numeric characters, handle duplicates,
   * then write the parsed number back to the model so ngModel stays numeric.
   */
  onFieldInput(event: Event, model: any, key: string): void {
    const input = event.target as HTMLInputElement;
    let sanitized = input.value.replace(/[^0-9.\-]/g, '');

    // Keep only the first decimal point
    const dotIdx = sanitized.indexOf('.');
    if (dotIdx !== -1) {
      sanitized = sanitized.substring(0, dotIdx + 1) +
                  sanitized.substring(dotIdx + 1).replace(/\./g, '');
    }

    // Keep only a leading minus sign
    const hasLeadingMinus = sanitized.startsWith('-');
    sanitized = sanitized.replace(/-/g, '');
    if (hasLeadingMinus) {
      sanitized = '-' + sanitized;
    }

    input.value = sanitized;
    model[key] = (sanitized === '' || sanitized === '-' || sanitized === '.' || sanitized === '-.')
      ? null
      : parseFloat(sanitized);
  }

  ngOnDestroy(): void {
    this.gtwWorkspace.hideDataFilters.next(false);
    this.subs.unsubscribe();
  }
}
